import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { MagicLinkChooser } from '../../../components/auth/MagicLinkChooser'
import { logError } from '../../../services/errorHandler'
import { buildSamaCoacheeDeepLink } from '../../../utils/auth/deep-link'

// Params we copy from inbound URL to the outbound coachee-web URL on
// "Continue in browser". `state`/`authority_url`/`client_id` are needed for
// Microsoft SSO: `state` for OAuth state validation on the post-do_login
// callback, `authority_url` (the full Microsoft authority, SAM-403) and
// `client_id` so the mobile apps can configure native MSAL for custom
// (per-company) authorities; `lng` preserves language across the bounce.
const allowedParams = [
    'details',
    'phoneNumber',
    'magicToken',
    'state',
    'authority_url',
    'client_id',
    'lng',
] as const

const loginUrlMap: Record<string, string | undefined> = {
    '/coachee/login': import.meta.env.VITE_APP_COACHEE_WEB_LOGIN_URL,
    '/coachee/verify-password': import.meta.env
        .VITE_APP_COACHEE_WEB_VERIFY_PASSWORD_URL,
    '/coachee/auth/okta/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_OKTA_LOGIN_URL,
    '/coachee/auth/sama/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_AUTH_PROVIDOR_LOGIN_URL,
    '/coachee/auth/sso/microsoft': import.meta.env
        .VITE_APP_COACHEE_WEB_MS_SSO_URL,
}

export const LoginCoacheePage: React.FC = observer(() => {
    const { i18n } = useTranslation()
    const [search] = useSearchParams()
    const { pathname } = useLocation()
    const { companyId, ssoId } = useParams<{
        companyId: string
        ssoId: string
    }>()
    // Presence of the param — not its value — determines one-time mode, which
    // matches the chooser's documented contract. An empty `?magicToken=` still
    // counts as a magic-token landing and must lock the one-time flow.
    const hasMagicTokenParam = search.has('magicToken')
    const isOneTimeMagicTokenLink = hasMagicTokenParam
    const language = i18n.resolvedLanguage
    // i18n.resolvedLanguage can be a region-qualified locale ("en-GB",
    // "fr-CA"); detect French explicitly so anything non-French (incl.
    // en-GB / en-US) gets the English store URL.
    const isFrench = language?.toLowerCase().startsWith('fr') ?? false

    const playStoreAppLink = isFrench
        ? import.meta.env.VITE_APP_FR_PLAY_STORE_URL
        : import.meta.env.VITE_APP_EN_PLAY_STORE_URL

    const appStoreAppLink = isFrench
        ? import.meta.env.VITE_APP_FR_APP_STORE_URL
        : import.meta.env.VITE_APP_EN_APP_STORE_URL

    const getUrl = useCallback(() => {
        // Resolve the web continue URL. The company-SSO route is dynamic
        // (companyId + ssoId from useParams); build it from the env base.
        let url: string | undefined
        if (companyId && ssoId) {
            const base = import.meta.env.VITE_APP_COACHEE_WEB_COMPANY_SSO_URL
            if (base) {
                url = `${base}/${encodeURIComponent(companyId)}/${encodeURIComponent(ssoId)}`
            }
        } else {
            url = loginUrlMap[pathname]
        }

        if (hasMagicTokenParam) {
            // Magic-token landings always forward to coachee-web's magic-login
            // route, regardless of which auth route the user originally hit.
            url = import.meta.env.VITE_APP_COACHEE_WEB_MAGIC_LOGIN_URL
        }

        if (!url) return ''

        const urlObject = new URL(url)
        urlObject.searchParams.set('do_login', 'true')

        for (const param of allowedParams) {
            if (search.has(param)) {
                urlObject.searchParams.set(param, search.get(param) ?? '')
            }
        }

        return urlObject.toString()
    }, [companyId, pathname, ssoId, search, hasMagicTokenParam])

    // do_login=true means coachee-web bounced us back (rare). Forward straight
    // to the per-route web URL instead of re-rendering the chooser.
    const directForward = search.has('do_login')
    // Track whether the direct-forward redirect actually fired. If getUrl()
    // returns empty (env misconfig — no per-route mapping for the current
    // pathname) we'd otherwise render `null` and the user sees a blank page
    // indefinitely. Falling back to the chooser at least lets them choose
    // manually, and we log to Sentry so the misconfig is visible.
    const [redirectFailed, setRedirectFailed] = useState(false)

    useEffect(() => {
        if (directForward) {
            const url = getUrl()
            if (url) {
                window.location.replace(url)
            } else {
                logError(
                    new Error(
                        'LoginCoacheePage: directForward had no URL to redirect to',
                    ),
                    { pathname },
                )
                setRedirectFailed(true)
            }
        }
    }, [directForward, getUrl, pathname])

    const deepLinkUrl = buildSamaCoacheeDeepLink(pathname, search, {
        companyId,
        ssoId,
    })

    const handleContinueOnWeb = useCallback(() => {
        const url = getUrl()
        if (!url) {
            logError(
                new Error(
                    'LoginCoacheePage: continueOnWeb had no URL to redirect to',
                ),
                { pathname },
            )
            return
        }
        window.location.href = url
    }, [getUrl, pathname])

    if (directForward && !redirectFailed) {
        // Render nothing while the useEffect fires window.location.replace.
        // If the redirect couldn't fire (env misconfig — see useEffect above)
        // we fall through to the chooser as a degraded fallback.
        return null
    }

    return (
        <MagicLinkChooser
            deepLinkUrl={deepLinkUrl}
            onContinueOnWeb={handleContinueOnWeb}
            appStoreUrl={appStoreAppLink}
            playStoreUrl={playStoreAppLink}
            isOneTimeMagicTokenLink={isOneTimeMagicTokenLink}
        />
    )
})

export default LoginCoacheePage
